import { hostname, userInfo } from 'os';
import type { BrandConfig } from '../branding/types';
import { renderTemplate } from '../branding/apply';
import { checkResponseMinVersion } from '../version-check';
import type { PairingConfig } from './store';

/**
 * Device-code OAuth pairing against Keep.
 *
 * The flow:
 *   1. POST {keepApiUrl}/api/oauth/device with client_id + scope.
 *      (Or: if the caller passed a server-issued pairing code from the
 *      product UI, POST /api/oauth/device/redeem instead.)
 *   2. Print user_code and verification_uri with branded strings.
 *   3. Poll the token endpoint until success or timeout.
 *   4. Persist credentials + bound identity + discovered pools.
 *
 * The product's `postSignupHook` (see sifter-provisioning.md) has already
 * created the service identity and role binding. The Sifter only consumes
 * the binding; it does not provision identities itself.
 */
export interface PairOptions {
  brand: BrandConfig;
  pairingCode?: string;
  /** Override polling interval for tests. */
  pollIntervalMs?: number;
  /** Override timeout for tests. */
  timeoutMs?: number;
  /** Defaults to fetch(); test seam. */
  fetcher?: typeof fetch;
  /** Defaults to console.log; CLI passes its own. */
  log?: (line: string) => void;
}

interface DeviceCodeResponse {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  interval: number;
}

interface DeviceTokenResponse {
  clientId: string;
  clientSecret: string;
  serviceIdentity: {
    id: string;
    name: string;
    displayName: string;
  };
  jobPools: Array<{ id: string; name?: string }>;
}

export async function pair(opts: PairOptions): Promise<PairingConfig> {
  const fetcher = opts.fetcher ?? globalThis.fetch;
  const log = opts.log ?? ((line: string) => console.log(line));
  const reg = opts.brand.keepRegistration;
  const displayName = renderTemplate(reg.serviceIdentityDisplayName, {
    user: userInfo().username,
    hostname: hostname(),
  });
  const pairingCode = opts.pairingCode;

  let token: DeviceTokenResponse;
  if (pairingCode) {
    token = await redeemPairingCode({
      keepApiUrl: reg.keepApiUrl,
      oauthClientId: reg.oauthClientId,
      userCode: pairingCode,
      displayName,
      fetcher,
      brand: opts.brand,
    });
    log(opts.brand.strings?.pairingDoneMessage ?? `Paired ${displayName}.`);
  } else {
    const device = await startDeviceCode({
      keepApiUrl: reg.keepApiUrl,
      oauthClientId: reg.oauthClientId,
      scopes: reg.scopes,
      fetcher,
      brand: opts.brand,
    });
    log(opts.brand.strings?.pairingHeader ?? `Pair your ${opts.brand.product.title}`);
    log('');
    log(`Visit:     ${device.verificationUri}`);
    log(`User code: ${device.userCode}`);
    log('');
    log(`Waiting for verification (expires in ${device.expiresIn}s)...`);

    token = await pollForToken({
      keepApiUrl: reg.keepApiUrl,
      oauthClientId: reg.oauthClientId,
      deviceCode: device.deviceCode,
      intervalMs: opts.pollIntervalMs ?? device.interval * 1000,
      timeoutMs: opts.timeoutMs ?? device.expiresIn * 1000,
      displayName,
      fetcher,
      brand: opts.brand,
    });
    log(opts.brand.strings?.pairingDoneMessage ?? `Paired ${displayName}.`);
  }

  return {
    schemaVersion: 1,
    keepApiUrl: reg.keepApiUrl,
    jobsApiUrl: reg.jobsApiUrl,
    oauthClientId: reg.oauthClientId,
    serviceIdentity: token.serviceIdentity,
    credentials: {
      clientId: token.clientId,
      clientSecret: token.clientSecret,
    },
    pairing: {
      pairedAt: new Date().toISOString(),
      hostname: hostname(),
      user: userInfo().username,
    },
    jobPools: token.jobPools,
  };
}

async function startDeviceCode(opts: {
  keepApiUrl: string;
  oauthClientId: string;
  scopes: ReadonlyArray<string>;
  fetcher: typeof fetch;
  brand: BrandConfig;
}): Promise<DeviceCodeResponse> {
  const url = `${opts.keepApiUrl.replace(/\/$/, '')}/api/oauth/device`;
  const response = await opts.fetcher(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: opts.oauthClientId,
      scope: opts.scopes.join(' '),
    }),
  });
  checkResponseMinVersion({
    response,
    currentVersion: opts.brand.product.version,
    packageName: opts.brand.product.packageName,
  });
  const json = await readJson<{ data: DeviceCodeResponse }>(response);
  return json.data;
}

async function pollForToken(opts: {
  keepApiUrl: string;
  oauthClientId: string;
  deviceCode: string;
  intervalMs: number;
  timeoutMs: number;
  displayName: string;
  fetcher: typeof fetch;
  brand: BrandConfig;
}): Promise<DeviceTokenResponse> {
  const url = `${opts.keepApiUrl.replace(/\/$/, '')}/api/oauth/device/token`;
  const deadline = Date.now() + opts.timeoutMs;

  while (Date.now() < deadline) {
    const response = await opts.fetcher(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: opts.oauthClientId,
        device_code: opts.deviceCode,
        display_name: opts.displayName,
        hostname: hostname(),
        os_user: userInfo().username,
      }),
    });

    if (response.status === 202) {
      // Still pending. Sleep and try again.
      await sleep(opts.intervalMs);
      continue;
    }
    if (response.status === 410) {
      throw new Error('Pairing code expired before verification.');
    }
    checkResponseMinVersion({
      response,
      currentVersion: opts.brand.product.version,
      packageName: opts.brand.product.packageName,
    });
    const json = await readJson<{ data: DeviceTokenResponse }>(response);
    return json.data;
  }

  throw new Error('Pairing timed out before verification.');
}

async function redeemPairingCode(opts: {
  keepApiUrl: string;
  oauthClientId: string;
  userCode: string;
  displayName: string;
  fetcher: typeof fetch;
  brand: BrandConfig;
}): Promise<DeviceTokenResponse> {
  const url = `${opts.keepApiUrl.replace(/\/$/, '')}/api/oauth/device/redeem`;
  const response = await opts.fetcher(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: opts.oauthClientId,
      user_code: opts.userCode,
      display_name: opts.displayName,
      hostname: hostname(),
      os_user: userInfo().username,
    }),
  });
  checkResponseMinVersion({
    response,
    currentVersion: opts.brand.product.version,
    packageName: opts.brand.product.packageName,
  });
  const json = await readJson<{ data: DeviceTokenResponse }>(response);
  return json.data;
}

async function readJson<T>(response: Response): Promise<T> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(`Pairing endpoint returned non-JSON (status ${response.status}).`);
  }
  if (!response.ok) {
    const err = body as { error?: { message?: string } };
    throw new Error(
      err.error?.message ?? `Pairing endpoint returned status ${response.status}.`,
    );
  }
  return body as T;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
